import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Trans, useLingui } from "@lingui/react/macro";
import MobileAppBar from "../conversation/MobileAppBar";
import DesktopLogoConversationPanel from "../conversation/DesktopLogoConversationPanel";
import { checkEconomicIntegration } from "../../store/economic.tsx";
import { checkIntempusIntegration } from "../../store/intempus.tsx";
import { checkFenerumIntegration } from "../../store/fenerum.tsx";
import { checkRackbeatIntegration } from "../../store/rackbeat.tsx";
import { checkBusinessCentralIntegration } from "../../store/business_central.tsx";
import type { RootState, AppDispatch } from "../../store/types.tsx";
import { INTEGRATIONS } from "./integrationMetadata.ts";
import IntegrationCard from "./IntegrationCard.tsx";
import IntegrationDetailModal from "./IntegrationDetailModal.tsx";
import type { IntegrationMeta } from "./integrationMetadata.ts";
import SETTINGS from "../../lib/TallyLibrary/constants/configGeneral.json";
import { getEconomicAuthUrl } from "./economicUtils.ts";
import { pushAnalyticsEvent } from "../../utils/googleEvent.ts";
import IntempusConnectDialog from "../conversation/IntempusConnectDialog.tsx";
import ApiKeyConnectDialog from "./ApiKeyConnectDialog.tsx";
import axios from "../../utils/axios";
import { useFeatureFlag } from "../../hooks/useFeatureFlag.tsx";

interface ConversationPanelAnimationRef {
  start: () => void;
  stop: () => void;
}

interface IntegrationsPageProps {
  openDrawer?: boolean;
  setOpenDrawer: React.Dispatch<React.SetStateAction<boolean>>;
}

const IntegrationsPage: React.FC<IntegrationsPageProps> = ({
  openDrawer = false,
  setOpenDrawer,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useLingui();
  const isBusinessCentralEnabled = useFeatureFlag("business_central");
  const theme = useTheme();
  const isScreenBiggerThanOrEqualToMd = useMediaQuery(
    theme.breakpoints.up("md"),
  );
  const isScreenBiggerThanOrEqualToLg = useMediaQuery(
    theme.breakpoints.up("lg"),
  );

  const { hasIntegration: hasEconomic, isLoading: isLoadingEconomic } =
    useSelector((state: RootState) => state.economic);
  const { hasIntegration: hasIntempus, isLoading: isLoadingIntempus } =
    useSelector((state: RootState) => state.intempus);
  const { hasIntegration: hasFenerum, isLoading: isLoadingFenerum } =
    useSelector((state: RootState) => state.fenerum);
  const { hasIntegration: hasRackbeat, isLoading: isLoadingRackbeat } =
    useSelector((state: RootState) => state.rackbeat);
  const {
    hasIntegration: hasBusinessCentral,
    isLoading: isLoadingBusinessCentral,
  } = useSelector((state: RootState) => state.businessCentral);

  const [selectedIntegration, setSelectedIntegration] =
    useState<IntegrationMeta | null>(null);
  const [isIntempusDialogOpen, setIsIntempusDialogOpen] = useState(false);
  const [isFenerumDialogOpen, setIsFenerumDialogOpen] = useState(false);
  const [isRackbeatDialogOpen, setIsRackbeatDialogOpen] = useState(false);

  // Animation ref for the Tally logo
  const refAnimation = useRef<ConversationPanelAnimationRef>(null);

  const handleStartAnimation = useCallback(() => {
    refAnimation.current?.start();
  }, []);

  const handleStopAnimation = useCallback(() => {
    refAnimation.current?.stop();
  }, []);

  // Fetch connection status for all integrations on mount
  useEffect(() => {
    dispatch(checkEconomicIntegration());
    dispatch(checkIntempusIntegration());
    dispatch(checkFenerumIntegration());
    dispatch(checkRackbeatIntegration());
    if (isBusinessCentralEnabled) {
      dispatch(checkBusinessCentralIntegration());
    }
  }, [dispatch, isBusinessCentralEnabled]);

  const visibleIntegrations = useMemo(
    () =>
      INTEGRATIONS.filter(
        (integration) =>
          integration.identifier !== "business_central" ||
          isBusinessCentralEnabled,
      ),
    [isBusinessCentralEnabled],
  );

  useEffect(() => {
    if (
      !isBusinessCentralEnabled &&
      selectedIntegration?.identifier === "business_central"
    ) {
      setSelectedIntegration(null);
    }
  }, [isBusinessCentralEnabled, selectedIntegration]);

  const handleConnectEconomic = useCallback(() => {
    pushAnalyticsEvent("integration_connect_started", {
      integration: "e-conomic",
    });
    window.open(getEconomicAuthUrl(), "_blank", "noopener,noreferrer");
  }, []);

  const handleConnectIntempus = useCallback(() => {
    pushAnalyticsEvent("integration_connect_started", {
      integration: "intempus",
    });
    setIsIntempusDialogOpen(true);
  }, []);

  const handleIntempusConnectSuccess = useCallback(() => {
    dispatch(checkIntempusIntegration());
    setIsIntempusDialogOpen(false);
    setSelectedIntegration(null);
  }, [dispatch]);

  const handleConnectFenerum = useCallback(() => {
    pushAnalyticsEvent("integration_connect_started", {
      integration: "fenerum",
    });
    setIsFenerumDialogOpen(true);
  }, []);

  const handleFenerumConnectSuccess = useCallback(() => {
    dispatch(checkFenerumIntegration());
    setIsFenerumDialogOpen(false);
    setSelectedIntegration(null);
  }, [dispatch]);

  const handleConnectRackbeat = useCallback(() => {
    pushAnalyticsEvent("integration_connect_started", {
      integration: "rackbeat",
    });
    setIsRackbeatDialogOpen(true);
  }, []);

  const handleRackbeatConnectSuccess = useCallback(() => {
    dispatch(checkRackbeatIntegration());
    setIsRackbeatDialogOpen(false);
    setSelectedIntegration(null);
  }, [dispatch]);

  const handleConnectBusinessCentral = useCallback(async () => {
    pushAnalyticsEvent("integration_connect_started", {
      integration: "business_central",
    });
    try {
      const response = await axios.get<{ authorize_url?: string }>(
        "/v1/integrations/business_central/authorize/",
      );
      const authorizeUrl = response.data.authorize_url;
      if (authorizeUrl) {
        window.location.href = authorizeUrl;
      }
    } catch (error) {
      console.error("Failed to get Business Central authorization URL:", error);
    }
  }, []);

  const getOnConnect = useCallback(
    (identifier: string, isConnected: boolean): (() => void) | undefined => {
      if (isConnected) return undefined;
      switch (identifier) {
        case "economic":
          return handleConnectEconomic;
        case "intempus":
          return handleConnectIntempus;
        case "fenerum":
          return handleConnectFenerum;
        case "rackbeat":
          return handleConnectRackbeat;
        case "business_central":
          return handleConnectBusinessCentral;
        default:
          return undefined;
      }
    },
    [
      handleConnectEconomic,
      handleConnectIntempus,
      handleConnectFenerum,
      handleConnectRackbeat,
      handleConnectBusinessCentral,
    ],
  );

  const getConnectionStatus = (
    identifier: string,
  ): { isConnected: boolean; isLoading: boolean } => {
    switch (identifier) {
      case "economic":
        return { isConnected: hasEconomic, isLoading: isLoadingEconomic };
      case "intempus":
        return { isConnected: hasIntempus, isLoading: isLoadingIntempus };
      case "fenerum":
        return { isConnected: hasFenerum, isLoading: isLoadingFenerum };
      case "google_sheets":
        // Always show as "Available" — no API call due to side effects
        return { isConnected: false, isLoading: false };
      case "rackbeat":
        return { isConnected: hasRackbeat, isLoading: isLoadingRackbeat };
      case "business_central":
        return {
          isConnected: hasBusinessCentral,
          isLoading: isLoadingBusinessCentral,
        };
      default:
        return { isConnected: false, isLoading: false };
    }
  };

  return (
    <Box>
      {isScreenBiggerThanOrEqualToLg && (
        <DesktopLogoConversationPanel refAnimation={refAnimation} />
      )}
      <Box
        sx={[
          {
            position: "fixed",
            top: "50%",
            left: "calc(50%)",
            transform: "translate(-50%, -50%)",
            height: "100%",
            width: {
              xs: "100vw",
              md: "min(92vw, 980px)",
              lg: "min(82vw, 1080px)",
              xl: "min(78vw, 1240px)",
            },
            backgroundColor: theme.palette.secondary.main,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            transition: theme.transitions.create(["left", "width"], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          },
          isScreenBiggerThanOrEqualToMd &&
            openDrawer && {
              left: `calc(50% + ${SETTINGS.drawerWidth / 2}px)`,
              width: {
                md: `min(calc(92vw - ${SETTINGS.drawerWidth}px), 980px)`,
                lg: `min(calc(82vw - ${SETTINGS.drawerWidth}px), 1080px)`,
                xl: `min(calc(78vw - ${SETTINGS.drawerWidth}px), 1240px)`,
              },
              transition: theme.transitions.create(["left", "width"], {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
        ]}
      >
        {!isScreenBiggerThanOrEqualToLg && (
          <MobileAppBar
            openDrawer={openDrawer}
            anonymousSession={false}
            isScreenBiggerThanOrEqualToLg={isScreenBiggerThanOrEqualToLg}
            handleDrawerOpen={() => setOpenDrawer(true)}
          />
        )}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 },
            pt: { xs: `${SETTINGS.topMarginMobile}px`, lg: 4 },
            pb: { xs: 3, sm: 4 },
            scrollbarColor: "var(--tally-brown) var(--tally-white)",
          }}
        >
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" fontWeight="bold" color="text.primary">
              <Trans context="Integrations page title">Integrations</Trans>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              <Trans context="Integrations page subtitle">
                Connect your apps and services so Tally can access your data.
              </Trans>
            </Typography>
          </Box>

          {/* Card grid */}
          <Grid container spacing={2}>
            {visibleIntegrations.map((integration) => {
              const { isConnected, isLoading } = getConnectionStatus(
                integration.identifier,
              );
              return (
                <Grid
                  key={integration.identifier}
                  size={{ xs: 12, sm: 6, md: 6 }}
                >
                  <IntegrationCard
                    integration={integration}
                    isConnected={isConnected}
                    isLoading={isLoading}
                    onClick={() => setSelectedIntegration(integration)}
                  />
                </Grid>
              );
            })}
          </Grid>

          {/* Detail modal */}
          {selectedIntegration && (
            <IntegrationDetailModal
              open={!!selectedIntegration}
              onClose={() => setSelectedIntegration(null)}
              integration={selectedIntegration}
              isConnected={
                getConnectionStatus(selectedIntegration.identifier).isConnected
              }
              onConnect={getOnConnect(
                selectedIntegration.identifier,
                getConnectionStatus(selectedIntegration.identifier).isConnected,
              )}
              onStartAnimation={handleStartAnimation}
              onStopAnimation={handleStopAnimation}
            />
          )}

          {/* Intempus connect dialog */}
          <IntempusConnectDialog
            open={isIntempusDialogOpen}
            onClose={() => setIsIntempusDialogOpen(false)}
            onSuccess={handleIntempusConnectSuccess}
          />

          {/* Fenerum connect dialog */}
          <ApiKeyConnectDialog
            open={isFenerumDialogOpen}
            onClose={() => setIsFenerumDialogOpen(false)}
            onSuccess={handleFenerumConnectSuccess}
            integrationIdentifier="fenerum"
            integrationName="Fenerum"
            helpText={t`To connect your Fenerum account, you'll need your API key. You can find it in your Fenerum account settings.`}
            helpUrl="https://app.fenerum.com/"
          />

          {/* Rackbeat connect dialog */}
          <ApiKeyConnectDialog
            open={isRackbeatDialogOpen}
            onClose={() => setIsRackbeatDialogOpen(false)}
            onSuccess={handleRackbeatConnectSuccess}
            integrationIdentifier="rackbeat"
            integrationName="Rackbeat"
            helpText={t`To connect your Rackbeat account, you'll need your API key. You can find it in your Rackbeat settings.`}
            helpUrl="https://app.rackbeat.com/"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default IntegrationsPage;
